import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import {
  GiftSettlementStatus,
  GiftSupplySettlement,
  GiftSupplySettlementPatchBody,
  SettlementPendingEntry,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

import { formatDate, getItemName } from '../../utils/delivery.utils';
import { formatWon, floorWon } from '../../utils/settlement.utils';

// ─────────────────────────────────────────────
// SettlementEditModal — 정산 1건 수정(업체/상조 공용)
//
// 한 정산 문서를 모달에서:
//  - 현재 라인 보기 / 제거 (removeDeliveryIds)
//  - 미정산 배송 추가 (addDeliveryIds)
//  - 메모 / 정산월(year·month) 수정
//  - 지급완료(Paid) → Draft 되돌리기 / 삭제(Draft 한정)
// 로 편집한다. 저장 시 백엔드가 라인·합계를 재계산하므로 부모는 onSaved로 refetch만 하면 된다.
// 라인 표시는 동결 스냅샷(line.item)만 사용 → 추가 populate 불필요.
// ─────────────────────────────────────────────

type AnySettlement = GiftSupplySettlement | GiftRebateSettlement;
type AnyLine = AnySettlement['lines'][number];

export interface SettlementEditModalProps {
  open: boolean;
  /** 업체정산(supply) / 상조정산(rebate) — 라벨/안내 문구용 */
  purpose: 'supply' | 'rebate';
  settlement: AnySettlement;
  targetName: string;
  /** 이 대상의 미정산(Delivered) 배송 조회 */
  fetchEligible: () => Promise<SettlementPendingEntry[]>;
  patch: (id: string, body: GiftSupplySettlementPatchBody) => Promise<unknown>;
  remove: (id: string) => Promise<void>;
  onClose: () => void;
  /** 저장/삭제/되돌리기 성공 후 부모 내역 refetch */
  onSaved: () => void;
}

/** 라인이 참조하는 배송 id (populate 여부 무관) */
const lineDeliveryId = (line: AnyLine): string | undefined => {
  const gd = line.giftDelivery;
  if (!gd) return undefined;
  return typeof gd === 'string' ? gd : gd._id;
};

export const SettlementEditModal: FC<SettlementEditModalProps> = ({
  open,
  purpose,
  settlement,
  targetName,
  fetchEligible,
  patch,
  remove,
  onClose,
  onSaved,
}) => {
  // Paid → Draft 되돌리기를 모달 안에서 즉시 반영하기 위한 로컬 상태
  const [localStatus, setLocalStatus] = useState<GiftSettlementStatus>(settlement.status);
  const [year, setYear] = useState(settlement.year);
  const [month, setMonth] = useState(settlement.month);
  const [memo, setMemo] = useState(settlement.memo ?? '');
  const [removeIds, setRemoveIds] = useState<Set<string>>(new Set());
  const [addIds, setAddIds] = useState<Set<string>>(new Set());
  // 실제 지급액(정산금액) 직접 입력 — amountTouched=true면 계산값 대신 이 값으로 동결(override)
  const [amount, setAmount] = useState<number>(settlement.totalAmount);
  const [amountTouched, setAmountTouched] = useState(false);

  const [eligible, setEligible] = useState<SettlementPendingEntry[] | null>(null);
  const [eligibleLoading, setEligibleLoading] = useState(false);

  const [busy, setBusy] = useState<null | 'save' | 'revert' | 'delete'>(null);
  const [error, setError] = useState<string | null>(null);

  const isDraft = localStatus === GiftSettlementStatus.Draft;
  // 업체/상조 라인 유니온 배열을 단일 타입으로 좁혀 map/reduce 호출 친화성 확보
  const lines = settlement.lines as AnyLine[];

  // 열릴 때마다 대상 정산 기준으로 초기화 + 미정산 배송 로드
  useEffect(() => {
    if (!open) return;
    setLocalStatus(settlement.status);
    setYear(settlement.year);
    setMonth(settlement.month);
    setMemo(settlement.memo ?? '');
    setRemoveIds(new Set());
    setAddIds(new Set());
    setAmount(settlement.totalAmount);
    setAmountTouched(false);
    setError(null);

    let alive = true;
    setEligibleLoading(true);
    fetchEligible()
      .then((entries) => {
        if (alive) setEligible(entries);
      })
      .catch(() => {
        if (alive) setEligible([]);
      })
      .finally(() => {
        if (alive) setEligibleLoading(false);
      });
    return () => {
      alive = false;
    };
    // settlement._id 가 바뀌거나 모달이 새로 열릴 때만 초기화
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settlement._id]);

  // 이미 이 정산에 들어있는 배송은 추가 후보에서 제외(방어적)
  const existingDeliveryIds = useMemo(() => {
    const s = new Set<string>();
    lines.forEach((l) => {
      const id = lineDeliveryId(l);
      if (id) s.add(id);
    });
    return s;
  }, [lines]);

  const eligibleEntries = useMemo(
    () => (eligible ?? []).filter((e) => !existingDeliveryIds.has(e.delivery._id)),
    [eligible, existingDeliveryIds],
  );

  // 저장 후 예상 건수/금액(근사치 — 정확한 금액은 백엔드 재계산)
  // 라인별 floorWon: 백엔드가 저장 시 건별 내림으로 재동결하므로, 개정 전 문서의
  // 소수 라인도 동일 규칙으로 예측한다(신규 정수 라인엔 no-op).
  const projection = useMemo(() => {
    const keptLines = lines.filter((l) => {
      const id = lineDeliveryId(l);
      return !id || !removeIds.has(id);
    });
    const keptAmount = keptLines.reduce((s, l) => s + floorWon(l.lineAmount), 0);
    const addedAmount = eligibleEntries
      .filter((e) => addIds.has(e.delivery._id))
      .reduce((s, e) => s + e.preview.lineAmount, 0);
    return {
      count: keptLines.length + addIds.size,
      amount: keptAmount + addedAmount,
    };
  }, [lines, removeIds, eligibleEntries, addIds]);

  // 라인을 추가/제거하면 금액(계산값)을 따라 갱신한다. 단, 관리자가 금액을 직접 입력(touched)한
  // 뒤에는 그 값을 유지한다 — '계산값으로' 버튼으로 다시 추종 상태로 되돌릴 수 있다.
  useEffect(() => {
    if (!amountTouched && (addIds.size > 0 || removeIds.size > 0)) {
      setAmount(projection.amount);
    }
  }, [projection.amount, amountTouched, addIds.size, removeIds.size]);

  if (!open) return null;

  const toggle = (setter: Dispatch<SetStateAction<Set<string>>>, id: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // 라인/정산월/메모 등 재계산 경로로 보낼 변경이 있는지
  const hasLineOrFieldChange =
    removeIds.size > 0 ||
    addIds.size > 0 ||
    memo !== (settlement.memo ?? '') ||
    year !== settlement.year ||
    month !== settlement.month;
  // 실제 지급액을 직접 입력해 동결할지 (계산값과 달라야 의미 있음)
  const amountOverridden = amountTouched && amount !== settlement.totalAmount;
  const dirty = hasLineOrFieldChange || amountOverridden;

  const handleSave = async () => {
    setBusy('save');
    setError(null);
    try {
      // 1) 라인/정산월/메모 변경 — 재계산 경로(add/remove 시 totalAmount 자동 재산정)
      if (hasLineOrFieldChange) {
        const body: GiftSupplySettlementPatchBody = { memo };
        if (year !== settlement.year) body.year = year;
        if (month !== settlement.month) body.month = month;
        if (addIds.size) body.addDeliveryIds = Array.from(addIds);
        if (removeIds.size) body.removeDeliveryIds = Array.from(removeIds);
        await patch(settlement._id, body);
      }
      // 2) 실제 지급액 직접 입력(override) — 라인 재계산 후 그 금액으로 동결한다.
      //    overrideAmount 경로는 add/remove를 무시하므로 반드시 1) 다음에 별도로 보낸다.
      if (amountOverridden) {
        await patch(settlement._id, { overrideAmount: true, totalAmount: amount });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  const handleRevert = async () => {
    setBusy('revert');
    setError(null);
    try {
      await patch(settlement._id, { status: GiftSettlementStatus.Draft });
      setLocalStatus(GiftSettlementStatus.Draft);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Draft 되돌리기에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `${year}년 ${month}월 ${targetName} 정산을 삭제할까요?\n포함된 배송은 다시 '정산 예정'으로 돌아갑니다.`,
      )
    )
      return;
    setBusy('delete');
    setError(null);
    try {
      await remove(settlement._id);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || '삭제에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  const unitLabel = purpose === 'supply' ? '도매단가' : '정산단가';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative my-4 flex max-h-[92vh] w-full max-w-[920px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
              <span className="truncate">{targetName}</span>
              <span
                className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${
                  isDraft ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
                }`}
              >
                {isDraft ? '정산집계' : '지급완료'}
              </span>
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              정산월 · 라인 · 메모를 수정합니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="닫기"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-auto px-5 py-4">
          {/* Paid 경고 */}
          {!isDraft && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-800">
                지급완료(Paid)된 정산은 수정할 수 없습니다. 먼저 Draft로 되돌리세요.
              </p>
              <button
                onClick={handleRevert}
                disabled={busy !== null}
                className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
              >
                {busy === 'revert' ? '처리 중...' : 'Draft로 되돌리기'}
              </button>
            </div>
          )}

          {/* 정산월 + 요약 + 정산금액 */}
          <div className="mb-5 space-y-3 rounded-lg bg-gray-50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-700">정산월</span>
                <input
                  type="number"
                  value={year}
                  min={2020}
                  max={2099}
                  disabled={!isDraft}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="w-20 rounded border border-gray-300 px-2 py-1 text-center disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-gray-500">년</span>
                <input
                  type="number"
                  value={month}
                  min={1}
                  max={12}
                  disabled={!isDraft}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="w-14 rounded border border-gray-300 px-2 py-1 text-center disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-gray-500">월</span>
              </div>
              <div className="text-right text-sm">
                <span className="text-gray-500">저장 후 예상 </span>
                <span className="font-semibold text-gray-800">{projection.count}건</span>
                <span className="text-gray-400"> · 계산 </span>
                <span className="font-semibold text-gray-700">
                  {formatWon(projection.amount)}
                </span>
              </div>
            </div>

            {/* 정산금액(실제 지급액) — 계산값과 다르면 이 금액으로 동결 */}
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3 text-sm">
              <span className="font-medium text-gray-700">정산금액(실제 지급액)</span>
              <input
                type="number"
                value={amount}
                min={0}
                step={1}
                disabled={!isDraft}
                onChange={(e) => {
                  setAmount(Math.max(0, Math.floor(Number(e.target.value) || 0)));
                  setAmountTouched(true);
                }}
                className="w-40 rounded border border-gray-300 px-2 py-1 text-right tabular-nums disabled:bg-gray-100 disabled:text-gray-400"
              />
              <span className="text-gray-500">원</span>
              {amountOverridden && (
                <>
                  <span className="text-xs text-amber-600">
                    계산값과 다름 — 입력한 금액으로 저장됩니다.
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAmount(projection.amount);
                      setAmountTouched(false);
                    }}
                    className="text-xs text-gray-500 underline hover:text-gray-700"
                  >
                    계산값으로
                  </button>
                </>
              )}
            </div>
          </div>

          {/* 현재 라인 */}
          <h3 className="mb-2 text-sm font-semibold text-gray-800">
            현재 정산 라인 ({lines.length}건)
          </h3>
          <div className="mb-6 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 break-keep text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  {isDraft && <th className="w-10 px-3 py-2">제거</th>}
                  <th className="px-3 py-2 text-left">상품</th>
                  <th className="px-3 py-2 text-right">{unitLabel}</th>
                  <th className="px-3 py-2 text-right">수량</th>
                  <th className="px-3 py-2 text-right">가산</th>
                  <th className="px-3 py-2 text-right">라인금액</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.length === 0 && (
                  <tr>
                    <td
                      colSpan={isDraft ? 6 : 5}
                      className="px-3 py-6 text-center text-gray-400"
                    >
                      라인이 없습니다.
                    </td>
                  </tr>
                )}
                {lines.map((l, idx) => {
                  const did = lineDeliveryId(l);
                  const marked = !!did && removeIds.has(did);
                  return (
                    <tr
                      key={did ?? idx}
                      className={
                        marked ? 'bg-rose-50 line-through opacity-60' : 'hover:bg-gray-50'
                      }
                    >
                      {isDraft && (
                        <td className="px-3 py-2 text-center">
                          {did ? (
                            <input
                              type="checkbox"
                              checked={marked}
                              onChange={() => toggle(setRemoveIds, did)}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          ) : (
                            <span className="text-[10px] text-gray-400">쿠폰</span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <p className="max-w-[260px] truncate font-medium text-gray-700">
                          {l.item?.name || '-'}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {l.unitAmount.toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {l.quantity}개
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {l.surcharge > 0 ? (
                          <span className="text-amber-600">
                            +{l.surcharge.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-gray-800">
                        {formatWon(l.lineAmount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 미정산 배송 추가 (Draft 한정) */}
          {isDraft && (
            <>
              <h3 className="mb-2 text-sm font-semibold text-gray-800">
                추가 가능한 미정산 배송
                {eligibleEntries.length > 0 && (
                  <span className="ml-1 text-gray-400">({eligibleEntries.length}건)</span>
                )}
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 break-keep text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="w-10 px-3 py-2">추가</th>
                      <th className="px-3 py-2 text-left">상품</th>
                      <th className="px-3 py-2 text-left">수령인</th>
                      <th className="px-3 py-2 text-left">배송완료일</th>
                      <th className="px-3 py-2 text-right">수량</th>
                      <th className="px-3 py-2 text-right">정산금액</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {eligibleLoading && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                          불러오는 중...
                        </td>
                      </tr>
                    )}
                    {!eligibleLoading && eligibleEntries.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                          추가할 미정산 배송이 없습니다.
                        </td>
                      </tr>
                    )}
                    {!eligibleLoading &&
                      eligibleEntries.map(({ delivery: d, preview: p }) => {
                        const checked = addIds.has(d._id);
                        return (
                          <tr
                            key={d._id}
                            className={`cursor-pointer ${
                              checked ? 'bg-indigo-50' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => toggle(setAddIds, d._id)}
                          >
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={checked}
                                readOnly
                                className="h-4 w-4 rounded border-gray-300"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <p className="max-w-[220px] truncate font-medium text-gray-700">
                                {getItemName(d)}
                              </p>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                              {d.recipient?.name || '-'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                              {formatDate(d.shipping?.deliveredAt)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                              {d.quantity}개
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-gray-800">
                              {formatWon(p.lineAmount)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* 메모 */}
          <div className="mt-6">
            <label className="mb-1 block text-sm font-semibold text-gray-800">메모</label>
            <textarea
              value={memo}
              disabled={!isDraft}
              onChange={(e) => setMemo(e.target.value)}
              rows={2}
              placeholder="정산 관련 메모 (선택)"
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
            />
          </div>

          {error && (
            <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </p>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
          <button
            onClick={handleDelete}
            disabled={!isDraft || busy !== null}
            className="rounded-lg border border-rose-300 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-40"
          >
            {busy === 'delete' ? '삭제 중...' : '정산 삭제'}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={busy !== null}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              닫기
            </button>
            <button
              onClick={handleSave}
              disabled={!isDraft || !dirty || busy !== null}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {busy === 'save' ? '저장 중...' : '변경사항 저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
